import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="16255" className="hidden md:flex gap-6">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-4 w-16 bg-primary/10 animate-pulse" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="relative text-xs font-medium uppercase tracking-widest text-foreground/70 transition-all duration-300 hover:text-primary font-mono py-2 after:absolute after:bottom-0 after:left-1/2 after:h-[1px] after:w-0 after:bg-primary/50 after:transition-all after:duration-300 after:-translate-x-1/2 hover:after:w-full hover:drop-shadow-[0_0_8px_rgba(34,211,238,0.5)]">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) return null;
  return <div className="flex flex-col gap-6 mt-8">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-sm font-medium uppercase tracking-[0.2em] text-foreground/60 hover:text-primary transition-all font-mono" onClick={onClose}>
          {item.label}
        </Link>)}
    </div>;
}
export default function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="system-header" className={`sticky top-0 left-0 right-0 z-50 transition-all duration-500 border-b dark bg-background text-foreground ${isScrolled ? "bg-background/80 backdrop-blur-xl border-primary/20 shadow-[0_0_20px_rgba(0,0,0,0.5)]" : "bg-background border-transparent"}`}>
      <div className="container mx-auto px-4 h-20 flex items-center justify-between">
        {}
        <Link to="/" className="flex items-center gap-2 z-50 group">
          <div className="relative p-1 border border-primary/20 bg-primary/5 transition-all duration-300 group-hover:border-primary/40 group-hover:bg-primary/10">
            {}
            <div className="absolute -top-[1px] -left-[1px] w-2 h-2 border-t border-l border-primary/60 transition-colors group-hover:border-primary" />
            <div className="absolute -bottom-[1px] -right-[1px] w-2 h-2 border-b border-r border-primary/60 transition-colors group-hover:border-primary" />
            
            <img src="/img/ai_neural_logo.png" alt="Tracy Vetter" className="h-19 w-auto object-contain transition-all duration-300 drop-shadow-[0_0_10px_rgba(34,211,238,0.4)] group-hover:drop-shadow-[0_0_15px_rgba(34,211,238,0.6)]" />
          </div>
        </Link>

        {}
        <MenuProvider menu_id="29" className="hidden md:block">
          <HeaderMenuConsumer />
        </MenuProvider>

        {}
        <div className="flex items-center gap-4">
          {}
          <div className="hidden md:block p-[1px] bg-gradient-to-r from-primary/10 via-primary/40 to-primary/10">
            <Button asChild variant="ghost" className="bg-background/40 hover:bg-primary/10 text-primary-foreground hover:text-primary h-9 px-6 font-mono text-xs tracking-widest transition-all border border-primary/20">
              <Link to="#">ACCESS TERMINAL</Link>
            </Button>
          </div>

          {}
          <Button variant="ghost" size="icon" className="md:hidden text-foreground/80 hover:text-primary hover:bg-transparent z-50" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
            {isMobileMenuOpen ? <X size={20} className="text-primary" /> : <Menu size={20} />}
          </Button>
        </div>
      </div>

      {}
      <div className={`fixed inset-0 bg-background/95 backdrop-blur-2xl z-40 transition-transform duration-500 md:hidden flex flex-col pt-24 px-8 border-l border-primary/10 ${isMobileMenuOpen ? "translate-x-0" : "translate-x-full"}`}>
        <MenuProvider menu_id="29">
          <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
        </MenuProvider>

        <div className="mt-12 p-[1px] bg-gradient-to-r from-primary/10 via-primary/50 to-primary/10 inline-block w-full max-w-xs">
          <Button asChild variant="ghost" className="w-full bg-background/60 hover:bg-primary/10 text-primary h-12 font-mono text-xs tracking-widest border border-primary/20">
            <Link to="#" onClick={() => setIsMobileMenuOpen(false)}>
              ACCESS TERMINAL
            </Link>
          </Button>
        </div>
      </div>
    </header>;
}